import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  ArticleWayback,
  useArchiveArticleMutation,
  useArchiveBookMutation,
  useGetArticleWaybackQuery,
  useGetBookWaybackQuery,
} from "../../services/rtkApi/blogApi";

/**
 * Runs the Wayback archive flow for one article and reports on it.
 *
 * Pressing the button queues a capture and returns immediately — Save Page Now
 * takes anywhere from ten seconds to a few minutes — so this polls the status
 * endpoint while the capture is in flight and stops as soon as it settles.
 *
 * The URL submitted is the backend's server-rendered permalink, not this SPA:
 * a CRA route archives as an empty shell. The component says so, because
 * "archived" pointing at a blank page would be worse than not archiving.
 *
 * Works for both archivable things — an article and a book's table of contents
 * — which differ only in the endpoint pair they hit.
 */

const POLL_INTERVAL_MS = 8_000;
/** SPN can legitimately take minutes; past this the scheduler owns the retry, not the tab. */
const MAX_POLL_MS = 6 * 60 * 1000;

type Props = {
  /** id of the article, or of the book when `kind` is "book" */
  articleId: string;
  kind?: "article" | "book";
  /** capture state already loaded by the parent, so the first paint is not blank */
  initialWayback?: ArticleWayback;
  compact?: boolean;
  disabled?: boolean;
  disabledReason?: string;
};

const WaybackArchiveButton: React.FC<Props> = ({
  articleId,
  kind = "article",
  initialWayback,
  compact = false,
  disabled = false,
  disabledReason,
}) => {
  const isBook = kind === "book";
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // hooks cannot be called conditionally, so both pairs are mounted and the
  // unused one is skipped
  const [archiveArticle, articleMutation] = useArchiveArticleMutation();
  const [archiveBook, bookMutation] = useArchiveBookMutation();
  const archive = isBook ? archiveBook : archiveArticle;
  const isQueueing = isBook ? bookMutation.isLoading : articleMutation.isLoading;

  const articleQuery = useGetArticleWaybackQuery(articleId, {
    skip: disabled || isBook,
  });
  const bookQuery = useGetBookWaybackQuery(articleId, {
    skip: disabled || !isBook,
  });
  const { data, refetch } = isBook ? bookQuery : articleQuery;

  const wayback = data?.wayback ?? initialWayback;
  const pending = !!wayback?.pending;

  // poll only while a capture is actually running, and give up after MAX_POLL_MS
  // so a stuck job cannot leave an interval running for the life of the tab
  useEffect(() => {
    if (!pending) {
      setStartedAt(null);
      return;
    }
    const began = startedAt ?? Date.now();
    if (startedAt === null) setStartedAt(began);

    const id = setInterval(() => {
      if (Date.now() - began > MAX_POLL_MS) {
        clearInterval(id);
        return;
      }
      refetch();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(id);
  }, [pending, startedAt, refetch]);

  const handleArchive = async () => {
    setError(null);
    try {
      await archive(articleId).unwrap();
      setStartedAt(Date.now());
      refetch();
    } catch (err: any) {
      setError(err?.data?.error || "could not start the archive run");
    }
  };

  const captured = wayback?.status === "captured";
  const failed = wayback?.status === "failed";

  const label = useMemo(() => {
    if (isQueueing) return "starting…";
    if (pending) return "archiving…";
    if (captured) return "archive again";
    if (failed) return "retry archive";
    return "archive to wayback";
  }, [isQueueing, pending, captured, failed]);

  const capturedAt = wayback?.capturedAt ? new Date(wayback.capturedAt) : null;
  const retryAt = wayback?.retryAt ? new Date(wayback.retryAt) : null;

  return (
    <Wrap $compact={compact}>
      <Row>
        <ArchiveButton
          type="button"
          onClick={handleArchive}
          disabled={disabled || isQueueing || pending}
          $state={captured ? "captured" : failed ? "failed" : "idle"}
          title={disabled ? disabledReason : wayback?.targetUrl}
        >
          {pending && <Spinner aria-hidden />}
          {label}
        </ArchiveButton>

        {captured && wayback?.snapshotUrl && (
          <SnapshotLink
            href={wayback.snapshotUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            view snapshot ↗
          </SnapshotLink>
        )}
      </Row>

      {disabled && disabledReason && <Note>{disabledReason}</Note>}

      {!disabled && (
        <>
          {pending && (
            <Note>
              {compact
                ? "submitted — usually under two minutes"
                : "submitted to the Internet Archive — this usually takes under two minutes. you can leave this page; it keeps running."}
            </Note>
          )}
          {captured && capturedAt && (
            <Note>
              captured {compact ? capturedAt.toLocaleDateString() : capturedAt.toLocaleString()}
              {!compact && wayback?.requestedBy ? ` · by @${wayback.requestedBy}` : ""}
            </Note>
          )}
          {failed && (
            <ErrorNote>
              archive failed after {wayback?.attempts ?? 0} attempts
              {!compact && wayback?.error ? ` — ${wayback.error}` : ""}
            </ErrorNote>
          )}
          {error && <ErrorNote>{error}</ErrorNote>}
          {retryAt && (
            <Note>
              {compact
                ? `retry at ${retryAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : `the last attempt did not go through; the next one is scheduled for ${retryAt.toLocaleString()}.`}
            </Note>
          )}
          {wayback?.targetUrl && !compact && (
            <Note>
              archives the static permalink{" "}
              <TargetLink
                href={wayback.targetUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {wayback.targetUrl}
              </TargetLink>{" "}
              — the site itself renders in the browser, so the snapshot uses the
              server-rendered copy of this {isBook ? "book" : "post"}.
            </Note>
          )}
        </>
      )}
    </Wrap>
  );
};

const Wrap = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: ${(p) => (p.$compact ? "0.78rem" : "0.85rem")};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ArchiveButton = styled.button<{ $state: "idle" | "captured" | "failed" }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid
    ${(p) =>
      p.$state === "captured" ? "#466a5d" : p.$state === "failed" ? "#a3453b" : "#555"};
  background: ${(p) =>
    p.$state === "captured" ? "#d4f9eb" : p.$state === "failed" ? "#fde3e0" : "white"};
  color: #222;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.25);
  transition: all 0.15s;

  &:hover:not(:disabled) {
    box-shadow: 3px 4px rgba(0, 0, 0, 0.35);
  }

  &:active:not(:disabled) {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: 1px 1px rgba(0, 0, 0, 0.15);
  }
`;

const Spinner = styled.span`
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: #466a5d;
  border-radius: 50%;
  animation: wb-spin 0.8s linear infinite;

  @keyframes wb-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SnapshotLink = styled.a`
  color: #466a5d;
  font-weight: 600;
  text-decoration: underline;
`;

const Note = styled.span`
  color: #777;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const TargetLink = styled.a`
  color: #666;
  word-break: break-all;
`;

const ErrorNote = styled.span`
  color: #a3453b;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

export default WaybackArchiveButton;
